import React, { useState, useRef, useEffect } from "react";
import AdminTopNavBar from "../common/AdminTopNavBar";
import "../../styles/Admin.css";
import AdminFooter from "../common/AdminFooter";
import AdminSideBar from "./AdminSideBar";
import getUserMenuItems from "./AdminMenuContent";
import { api, adminReqConfig, baseURL } from "../../include/api";
import { useNotification } from "../../Toast/ToastProvider";
import Spinner from "../add-ons/Spinner";
import { MinusCircle, PlusCircle } from "react-feather";
import { ERROR, SUCCESS } from "../../include/constant";
import { useNavigate } from "react-router-dom";

const decodeHtmlEntities = (str) => {
  if (!str || typeof str !== "string") return str;
  const textarea = document.createElement("textarea");
  textarea.innerHTML = str;
  return textarea.value;
};

const AddCategory = () => {
  const homeLink = "/admin";
  const [isSidebarMinimized, setIsSidebarMinimized] = useState(false);
  const [categoryName, setCategoryName] = useState("");
  const [description, setDescription] = useState("");
  const [image, setImage] = useState(null);
  const [mainImagePreview, setMainImagePreview] = useState("");
  const [status, setStatus] = useState(true);
  const [parentId, setParentId] = useState(0);
  const [categoryList, setCategoryList] = useState([]);
  const [fetchingCategory, setFetchingCategory] = useState(true);
  const [fieldGroups, setFieldGroups] = useState([
    { groupName: "", specs: [{ title: "", fieldType: "", options: "" }] },
  ]);
  const [saving, setSaving] = useState(false);
  const mainImageElement = useRef();
  const toast = useNotification();
  const urlParams = new URLSearchParams(window.location.search);
  const categoryIdFromUrl = urlParams.get("id");
  const isViewMode = urlParams.get("view") === "true";
  const isEditMode = !!categoryIdFromUrl && !isViewMode;
  const navigate = useNavigate();
  const [categoryId, setCategoryId] = useState(
    categoryIdFromUrl ? parseInt(categoryIdFromUrl, 10) : null,
  );
  const toggleSidebar = () => setIsSidebarMinimized(!isSidebarMinimized);
  const menuContent = getUserMenuItems();

  // BUTTON STYLING HELPERS
  const getButtonStyle = (isPrimary = false) => ({
    border: "none",
    padding: "8px 20px",
    fontWeight: "500",
    borderRadius: "5px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: saving ? "not-allowed" : "pointer",
    opacity: saving ? 0.7 : 1,
    ...(isPrimary 
      ? { 
          backgroundColor: saving ? "#1565c0" : "#1976d2", 
          color: "#fff" 
        } 
      : { 
          backgroundColor: saving ? "#e0e0e0" : "#bed9f8ff", 
          color: saving ? "#9e9e9e" : "#1976d2" 
        }
    ),
  });

  const renderLoadingSpinner = (size = "sm") => (
    <i 
      className={`fa fa-spinner fa-spin ${size === "sm" ? "me-2" : "me-3"}`} 
      aria-hidden="true"
      style={{ fontSize: size === "sm" ? "0.9rem" : "1.1rem" }}
    ></i>
  );

  const fetchCategories = () => {
    setFetchingCategory(true);
    api
      .get("/category/?flag=exceptHome", adminReqConfig())
      .then((res) => {
        if (res.status === 200) {
          setCategoryList(res.data.data || []);
        }
      })
      .catch(() => setCategoryList([]))
      .finally(() => setFetchingCategory(false));
  };

  const fetchCategorySpecs = (catId) => {
    api
      .get(`/category_specification/?categoryId=${catId}`, adminReqConfig())
      .then((res) => {
        if (res.status === 200) {
          const specs = res.data.data || [];
          const grouped = {};
          specs.forEach((spec) => {
            const decodedGroupName = decodeHtmlEntities(spec.groupName);
            if (!grouped[decodedGroupName]) {
              grouped[decodedGroupName] = [];
            }
            grouped[decodedGroupName].push({
              id: spec.id,
              title: decodeHtmlEntities(spec.title),
              fieldType: spec.fieldType,
              options: decodeHtmlEntities(spec.options) || "",
            });
          });

          const groups = Object.entries(grouped).map(([groupName, specs]) => ({
            groupName,
            specs,
          }));

          if (groups.length > 0) {
            setFieldGroups(groups);
          }
        }
      })
      .catch((err) => {
        console.warn("Failed to load specifications:", err);
      });
  };

  const fetchCategoryDetails = (id) => {
    api
      .get(`/category/?id=${id}`, adminReqConfig())
      .then((res) => {
        if (res.status === 200) {
          const cat = Array.isArray(res.data.data)
            ? res.data.data[0]
            : res.data.data;
          if (!cat) return;

          setCategoryName(decodeHtmlEntities(cat.name) || "");
          setParentId(cat.parentId || 0);
          setDescription(decodeHtmlEntities(cat.description) || "");

          const isActive = cat.status === "active";
          setStatus(isActive);

          if (cat.imageUrl) {
            setMainImagePreview(baseURL + "/" + cat.imageUrl);
            setImage(null);
          }

          fetchCategorySpecs(id);
        }
      })
      .catch((err) => {
        toast({ type: "error", message: "Failed to load category details." });
      });
  };

  useEffect(() => {
    fetchCategories();

    if (categoryId) {
      fetchCategoryDetails(categoryId);
    }
  }, [categoryId]);

  const handleMainImage = (files) => {
    if (saving || !files || files.length === 0) return;
    const file = files[0];
    if (
      !["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(
        file.type,
      )
    ) {
      toast({ type: "error", message: "Only JPG, PNG, WebP allowed" });
      if (mainImageElement.current) mainImageElement.current.value = "";
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      toast({ type: "error", message: "Image must be under 3MB" });
      if (mainImageElement.current) mainImageElement.current.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setMainImagePreview(reader.result);
      setImage(file);
    };
    reader.readAsDataURL(file);
    if (mainImageElement.current) mainImageElement.current.value = "";
  };

  const validate = () => {
    if (!categoryName.trim()) {
      toast({ type: "error", message: "Category name is required" });
      return false;
    }

    return true;
  };

  const handleSaveMainImage = (targetCategoryId) => {
    return new Promise((resolve, reject) => {
      if (!(image instanceof File)) return resolve(null);
      if (!targetCategoryId)
        return reject(new Error("No category ID to upload image for."));
      const data = new FormData();
      data.append("image", image);
      api
        .post(
          `/category/?id=${targetCategoryId}&flag=changeimage`,
          data,
          adminReqConfig(),
        )
        .then((response) => {
          if (response.status === 200) {
            toast({ type: SUCCESS, message: response.data.message });
            resolve(response.data);
          } else {
            reject(new Error("Unexpected response status: " + response.status));
          }
        })
        .catch((error) => {
          toast({
            type: ERROR,
            message: error.response?.data?.message || error.message,
          });
          reject(error);
        });
    });
  };

  const handleSave = async () => {
    if (!validate()) return;

    const validGroups = fieldGroups.filter((group) => {
      const validGroupName =
        typeof group.groupName === "string" &&
        group.groupName.trim().length > 0;
      const validSpecs = group.specs.filter((s) => {
        const hasTitle =
          typeof s.title === "string" && s.title.trim().length > 0;
        const hasFieldType =
          typeof s.fieldType === "string" && s.fieldType.trim().length > 0;
        const hasOptions =
          s.fieldType === "TEXT" ||
          (typeof s.options === "string" && s.options.trim().length > 0);
        return hasTitle && hasFieldType && hasOptions;
      });
      return validGroupName && validSpecs.length > 0;
    });

    setSaving(true);

    try {
      let savedCategoryId = null;
      if (isEditMode) {
        const payload = {
          name: categoryName.trim(),
          parentId: Number(parentId),
          description: description.trim(),
          status: status ? "active" : "inactive",
        };
        const updateRes = await api.put(
          `/category/?id=${categoryId}`,
          payload,
          adminReqConfig(),
        );
        if (updateRes.status !== 200) {
          throw new Error("Failed to update category");
        }
        savedCategoryId = categoryId;
      } else {
        const formData = new FormData();
        formData.append("name", categoryName.trim());
        formData.append("parentId", parentId);
        formData.append("description", description.trim());
        formData.append("status", status ? "active" : "inactive");
        if (image instanceof File) {
          formData.append("image", image);
        }
        const addRes = await api.post("/category/", formData, {
          ...adminReqConfig(),
          headers: { ...adminReqConfig().headers, "Content-Type": undefined },
        });
        if (addRes.status !== 200) {
          throw new Error("Failed to add category");
        }
        savedCategoryId = addRes.data?.data?.id || addRes.data?.id;
        if (!savedCategoryId) {
          throw new Error("Couldn't find new category ID from response");
        }
        setCategoryId(savedCategoryId);
      }

      if (isEditMode && image instanceof File) {
        await handleSaveMainImage(savedCategoryId);
      }

      if (validGroups.length > 0 && savedCategoryId) {
        if (isEditMode) {
          try {
            const existingSpecsRes = await api.get(
              `/category_specification/?categoryId=${savedCategoryId}`,
              adminReqConfig(),
            );

            const existingSpecs = existingSpecsRes.data.data || [];
            const existingSpecIds = existingSpecs.map((spec) => spec.id);

            const specPromises = [];
            validGroups.forEach((group) => {
              group.specs.forEach((spec) => {
                if (spec.id && existingSpecIds.includes(spec.id)) {
                  specPromises.push(
                    api.put(
                      `/category_specification/?id=${spec.id}`,
                      {
                        categoryId: savedCategoryId,
                        groupName: group.groupName.trim(),
                        title: spec.title.trim(),
                        fieldType: spec.fieldType,
                        options:
                          spec.fieldType === "TEXT" ? "" : spec.options || "",
                      },
                      adminReqConfig(),
                    ),
                  );
                } else {
                  specPromises.push(
                    api.post(
                      "/category_specification/",
                      {
                        categoryId: savedCategoryId,
                        groupName: group.groupName.trim(),
                        title: spec.title.trim(),
                        fieldType: spec.fieldType,
                        options:
                          spec.fieldType === "TEXT" ? "" : spec.options || "",
                      },
                      adminReqConfig(),
                    ),
                  );
                }
              });
            });

            await Promise.all(specPromises);
          } catch (error) {
            const specPromises = [];
            validGroups.forEach((group) => {
              group.specs.forEach((spec) => {
                if (spec.id) {
                  specPromises.push(
                    api.put(
                      `/category_specification/?id=${spec.id}`,
                      {
                        categoryId: savedCategoryId,
                        groupName: group.groupName.trim(),
                        title: spec.title.trim(),
                        fieldType: spec.fieldType,
                        options:
                          spec.fieldType === "TEXT" ? "" : spec.options || "",
                      },
                      adminReqConfig(),
                    ),
                  );
                } else {
                  specPromises.push(
                    api.post(
                      "/category_specification/",
                      {
                        categoryId: savedCategoryId,
                        groupName: group.groupName.trim(),
                        title: spec.title.trim(),
                        fieldType: spec.fieldType,
                        options:
                          spec.fieldType === "TEXT" ? "" : spec.options || "",
                      },
                      adminReqConfig(),
                    ),
                  );
                }
              });
            });

            await Promise.all(specPromises);
          }
        } else {
          const specPromises = validGroups.flatMap((group) =>
            group.specs
              .filter((s) => s.title.trim() && s.fieldType)
              .map((spec) =>
                api.post(
                  "/category_specification/",
                  {
                    categoryId: savedCategoryId,
                    groupName: group.groupName.trim(),
                    title: spec.title.trim(),
                    fieldType: spec.fieldType,
                    options:
                      spec.fieldType === "TEXT" ? "" : spec.options || "",
                  },
                  adminReqConfig(),
                ),
              ),
          );
          await Promise.all(specPromises);
        }
      } else if (isEditMode) {
        await api.delete(
          `/category_specification/?categoryId=${savedCategoryId}`,
          adminReqConfig(),
        );
      }

      toast({
        type: "success",
        message: isEditMode
          ? "Category updated successfully!"
          : "Category added successfully!",
      });

      navigate("/admin/category/categoryList");

      if (!isEditMode) clearFields();
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        (isEditMode ? "Failed to update category" : "Failed to add category");
      toast({ type: "error", message: msg });
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const clearFields = () => {
    if (saving) return;
    setCategoryName("");
    setParentId(0);
    setDescription("");
    setImage(null);
    setMainImagePreview("");
    setStatus(true);
    setFieldGroups([
      { groupName: "", specs: [{ title: "", fieldType: "", options: "" }] },
    ]);
  };

  const clearCatSpec = () => {
    if (saving) return;
    setFieldGroups([
      { groupName: "", specs: [{ title: "", fieldType: "", options: "" }] },
    ]);
  };

  const addSpecToGroup = (groupIndex) => {
    if (saving) return;
    const newGroups = [...fieldGroups];
    newGroups[groupIndex].specs.push({ title: "", fieldType: "", options: "" });
    setFieldGroups(newGroups);
  };

  const removeSpecFromGroup = (groupIndex, specIndex) => {
    if (saving) return;
    const newGroups = [...fieldGroups];
    if (newGroups[groupIndex].specs.length <= 1) return;
    newGroups[groupIndex].specs = newGroups[groupIndex].specs.filter(
      (_, i) => i !== specIndex,
    );
    setFieldGroups(newGroups);
  };

  const updateGroupField = (groupIndex, key, value) => {
    if (saving || isViewMode) return;
    const newGroups = [...fieldGroups];
    newGroups[groupIndex][key] = value;
    setFieldGroups(newGroups);
  };

  const updateSpecField = (groupIndex, specIndex, key, value) => {
    if (saving || isViewMode) return;
    const newGroups = [...fieldGroups];
    newGroups[groupIndex].specs[specIndex][key] = value;
    setFieldGroups(newGroups);
  };

  const addFieldGroup = () => {
    if (saving) return;
    setFieldGroups([
      ...fieldGroups,
      { groupName: "", specs: [{ title: "", fieldType: "", options: "" }] },
    ]);
  };

  const removeFieldGroup = (groupIndex) => {
    if (saving || fieldGroups.length <= 1) return;
    setFieldGroups(fieldGroups.filter((_, i) => i !== groupIndex));
  };

  const inputStyle = {
    borderRadius: "5px",
    fontWeight: "400",
    outline: "none",
    width: "100%",
    padding: "11px 24px",
    backgroundColor: "transparent !important",
    border: "1px solid var(--gray-100)",
    color: "hsl(var(--black))",
  };

  return (
    <>
      <AdminSideBar
        menuContent={menuContent}
        isMinimized={isSidebarMinimized}
      />
      <AdminTopNavBar
        toggleSidebar={toggleSidebar}
        isSidebarMinimized={isSidebarMinimized}
        homeLink={homeLink}
        title={
          isViewMode
            ? "CATEGORY DETAILS"
            : isEditMode
              ? "EDIT CATEGORY"
              : "ADD NEW CATEGORY"
        }
      />
      <div
        className="content-wrapper p-0"
        style={{ marginLeft: isSidebarMinimized ? "80px" : "250px", flex: "1" }}
      >
        <section
          style={{
            backgroundColor: "#F1F4F9",
            padding: "30px",
            marginTop: "70px",
            marginBottom: "40px",
          }}
        >
          {fetchingCategory ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ height: "50vh" }}
            >
              <Spinner color="primary" />
            </div>
          ) : (
            <div className="container-fluid">
              <div className="row">
                {/* Preview */}
                <div className="col-lg-3 mb-4">
                  <div
                    className="card p-8"
                    style={{ height: "400px", border: "none" }}
                  >
                    <div className="card-body d-flex flex-column align-items-start">
                      <div className="mb-3" style={{ textAlign: "center" }}>
                        {mainImagePreview ? (
                          <img
                            src={mainImagePreview}
                            alt="Category"
                            className="img-fluid rounded"
                            style={{
                              width: "190px",
                              height: "190px",
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <div
                            className="bg-light d-flex justify-content-center align-items-center"
                            style={{
                              width: "190px",
                              height: "190px",
                              borderRadius: "8px",
                            }}
                          >
                            <i
                              className="fa fa-image text-muted"
                              style={{ fontSize: "48px" }}
                            ></i>
                          </div>
                        )}
                      </div>
                      <h6
                        className="card-title fs-5 mt-16"
                        style={{ textAlign: "left", width: "100%" }}
                      >
                        {categoryName || "Category Name"}
                      </h6>
                    </div>
                  </div>
                </div>
                {/* Form */}
                <div className="col-lg-9">
                  <div className="card mb-22 pb-40 border-0">
                    <div className="d-flex justify-content-between align-items-center p-16">
                      {!isViewMode && (
                        <h6 className="mb-0 fs-5">
                          {isEditMode
                            ? "Edit Category Image"
                            : "Add Category Image"}
                        </h6>
                      )}
                      {isViewMode && (
                        <h6 className="mb-0 fs-5">Category Description</h6>
                      )}
                      {!isViewMode && (
                        <div className="d-flex align-items-center gap-2">
                          <span className="pe-10">Status</span>
                          <div className="form-check form-switch mb-0">
                            <input
                              className={`form-check-input form-product-status-switch ${
                                status === true
                                  ? "switch-green"
                                  : "switch-orange"
                              }`}
                              type="checkbox"
                              id="statusSwitch"
                              checked={status}
                              disabled={saving}
                              onChange={(e) => !saving && setStatus(e.target.checked)}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                    {isViewMode && (
                      <div className="ps-16 pe-16 pt-0 pb-0 mb-0">
                        <textarea
                          className="form-control border-0"
                          rows="6"
                          placeholder="Your Description Here..."
                          value={description}
                          readOnly
                        ></textarea>
                      </div>
                    )}
                    {!isViewMode && (
                      <div className="card-body" style={{ height: "300px" }}>
                        <div
                          className="border-dashed text-center cursor-pointer"
                          onClick={() => !saving && mainImageElement.current?.click()}
                          style={{
                            border: "2px dashed #ccc",
                            borderRadius: "8px",
                            transition: "border-color 0.2s",
                            minHeight: "270px",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            opacity: saving ? 0.6 : 1,
                            cursor: saving ? "not-allowed" : "pointer",
                          }}
                          onMouseEnter={(e) => {
                            if (!saving) e.currentTarget.style.borderColor = "#0d6efd";
                          }}
                          onMouseLeave={(e) => {
                            if (!saving) e.currentTarget.style.borderColor = "#ccc";
                          }}
                        >
                          {saving ? (
                            <>
                              <i 
                                className="fa fa-spinner fa-spin mb-3" 
                                style={{ fontSize: "48px", color: "#9e9e9e" }} 
                              ></i>
                              <h6>Processing...</h6>
                              <small className="text-muted">Please wait while saving...</small>
                            </>
                          ) : mainImagePreview ? (
                            <div className="d-flex flex-column align-items-center w-100">
                              <img
                                src={mainImagePreview}
                                alt="Main"
                                className="img-fluid mb-2"
                                style={{
                                  maxHeight: "180px",
                                  objectFit: "contain",
                                }}
                              />
                              <small className="text-muted">
                                Current category image
                              </small>
                            </div>
                          ) : (
                            <>
                              <i
                                className="fa fa-cloud-upload-alt mb-3"
                                style={{ fontSize: "48px", color: "#0d6efd" }}
                              ></i>
                              <h6>
                                Drop Your Images Here, Or{" "}
                                <span
                                  className="text-primary"
                                  style={{ cursor: "pointer" }}
                                >
                                  Click To Browse
                                </span>
                              </h6>
                              <small className="text-muted">
                                Square Pixels Recommended. PNG, JPG, WebP
                                allowed.
                              </small>
                            </>
                          )}
                        </div>
                        <input
                          type="file"
                          onChange={(e) => !saving && handleMainImage(e.target.files)}
                          className="d-none"
                          accept="image/*"
                          ref={mainImageElement}
                          disabled={saving}
                        />
                      </div>
                    )}
                  </div>

                  {!isViewMode && (
                    <div className="card border-0 mb-22">
                      <div className="d-flex justify-content-between align-items-center p-16">
                        <h6 className="mb-0 fs-5">Category Information</h6>
                        <button
                          onClick={clearFields}
                          disabled={saving}
                          style={getButtonStyle(false)}
                        >
                          {saving ? (
                            <>
                              {renderLoadingSpinner("sm")}
                              Clearing...
                            </>
                          ) : (
                            "Clear"
                          )}
                        </button>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          <div className="col-md-12 mb-16">
                            <h6 className="text-md mb-16 fw-bold">
                              Category Name
                            </h6>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Enter Category Name"
                              value={categoryName}
                              onChange={(e) => !saving && setCategoryName(e.target.value)}
                              style={inputStyle}
                              readOnly={isViewMode || saving}
                            />
                          </div>
                        </div>
                        <div className="mb-16">
                          <h6 className="text-md mb-16 fw-bold">
                            Category Description
                          </h6>
                          <textarea
                            className="form-control"
                            rows="4"
                            placeholder="Your Description Here..."
                            value={description}
                            onChange={(e) => !saving && setDescription(e.target.value)}
                            readOnly={isViewMode || saving}
                          ></textarea>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="card border-0">
                    <div className="d-flex justify-content-between align-items-center p-16">
                      <h6 className="mb-0 fs-5">Category Specification</h6>
                      {!isViewMode && (
                        <button
                          onClick={clearCatSpec}
                          disabled={saving}
                          style={getButtonStyle(false)}
                        >
                          {saving ? (
                            <>
                              {renderLoadingSpinner("sm")}
                              Clearing...
                            </>
                          ) : (
                            "Clear"
                          )}
                        </button>
                      )}
                    </div>
                    <div className="card-body">
                      {fieldGroups.map((group, groupIndex) => (
                        <div className="mb-24" key={groupIndex}>
                          <div className="row">
                            <div className="col-md-12 mb-16">
                              <h6 className="text-md mb-16 fw-bold">
                                Group Name
                              </h6>
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Enter Group Name"
                                value={group.groupName}
                                onChange={(e) =>
                                  !saving && updateGroupField(
                                    groupIndex,
                                    "groupName",
                                    e.target.value,
                                  )
                                }
                                style={inputStyle}
                                readOnly={isViewMode || saving}
                              />
                            </div>
                          </div>

                          {group.specs.map((spec, specIndex) => (
                            <div
                              className="row align-items-end mb-16"
                              key={specIndex}
                            >
                              <div className="col-md-3">
                                <h6 className="text-md mb-16 fw-bold">Title</h6>
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="Enter Title"
                                  value={spec.title}
                                  onChange={(e) =>
                                    !saving && updateSpecField(
                                      groupIndex,
                                      specIndex,
                                      "title",
                                      e.target.value,
                                    )
                                  }
                                  style={inputStyle}
                                  readOnly={isViewMode || saving}
                                />
                              </div>
                              <div className="col-md-4">
                                <h6 className="text-md mb-16 fw-bold">
                                  Field Type
                                </h6>
                                <select
                                  className="form-select"
                                  value={spec.fieldType}
                                  onChange={(e) =>
                                    !saving && updateSpecField(
                                      groupIndex,
                                      specIndex,
                                      "fieldType",
                                      e.target.value,
                                    )
                                  }
                                  style={inputStyle}
                                  disabled={isViewMode || saving}
                                >
                                  <option value="">--Select--</option>
                                  <option value="TEXT">TEXT</option>
                                  <option value="DROPDOWN LIST">
                                    DROPDOWN LIST
                                  </option>
                                  <option value="CHECKBOX">CHECKBOX</option>
                                  <option value="RADIO BUTTON">
                                    RADIO BUTTON
                                  </option>
                                </select>
                              </div>
                              {spec.fieldType !== "TEXT" && (
                                <div className="col-md-4">
                                  <h6 className="text-md mb-16 fw-bold">
                                    Options
                                  </h6>
                                  <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Comma-separated values"
                                    value={spec.options}
                                    onChange={(e) =>
                                      !saving && updateSpecField(
                                        groupIndex,
                                        specIndex,
                                        "options",
                                        e.target.value,
                                      )
                                    }
                                    style={inputStyle}
                                    disabled={isViewMode || saving}
                                  />
                                </div>
                              )}
                              {!isViewMode && (
                                <div className="col-md-1 d-flex align-items-end p-0 m-0">
                                  {group.specs.length > 1 && (
                                    <div
                                      className="mb-8"
                                      onClick={() =>
                                        !saving && removeSpecFromGroup(
                                          groupIndex,
                                          specIndex,
                                        )
                                      }
                                      style={{
                                        height: "fit-content",
                                        cursor: saving ? "not-allowed" : "pointer",
                                        opacity: saving ? 0.5 : 1,
                                      }}
                                    >
                                      <MinusCircle 
                                        style={{ 
                                          color: saving ? "#ccc" : "red",
                                          cursor: saving ? "not-allowed" : "pointer"
                                        }} 
                                      />
                                    </div>
                                  )}
                                  {specIndex === group.specs.length - 1 && (
                                    <div
                                      className="mb-8"
                                      onClick={() => 
                                        !saving && addSpecToGroup(groupIndex)
                                      }
                                      style={{
                                        height: "fit-content",
                                        cursor: saving ? "not-allowed" : "pointer",
                                        opacity: saving ? 0.5 : 1,
                                      }}
                                    >
                                      <PlusCircle 
                                        style={{ 
                                          color: saving ? "#ccc" : "blue",
                                          cursor: saving ? "not-allowed" : "pointer"
                                        }} 
                                      />
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}

                          {fieldGroups.length > 1 && !isViewMode && (
                            <div className="row">
                              <div className="col-md-12 text-end">
                                <button
                                  type="button"
                                  onClick={() => !saving && removeFieldGroup(groupIndex)}
                                  disabled={saving}
                                  style={{
                                    color: saving ? "#9e9e9e" : "#d32f2f",
                                    background: "none",
                                    border: "none",
                                    fontWeight: "500",
                                    cursor: saving ? "not-allowed" : "pointer",
                                    opacity: saving ? 0.7 : 1,
                                  }}
                                >
                                  Remove Group
                                </button>
                              </div>
                            </div>
                          )}

                          {groupIndex < fieldGroups.length - 1 && (
                            <hr className="my-24" />
                          )}
                        </div>
                      ))}

                      {!isViewMode && (
                        <div className="row">
                          <div className="col-md-12">
                            <button
                              type="button"
                              onClick={addFieldGroup}
                              disabled={saving}
                              style={{
                                backgroundColor: saving ? "#e0e0e0" : "#e3f2fd",
                                color: saving ? "#9e9e9e" : "#1976d2",
                                border: "none",
                                padding: "10px 16px",
                                fontWeight: "500",
                                borderRadius: "5px",
                                cursor: saving ? "not-allowed" : "pointer",
                                width: "100%",
                                textAlign: "center",
                                fontSize: "1rem",
                                opacity: saving ? 0.7 : 1,
                              }}
                            >
                              {saving ? (
                                <>
                                  {renderLoadingSpinner("sm")}
                                  Processing...
                                </>
                              ) : (
                                "+ Add New Specification Group"
                              )}
                            </button>
                          </div>
                        </div>
                      )}
                      {!isViewMode && (
                        <div className="d-flex justify-content-end gap-8 mt-16">
                          <button
                            onClick={handleSave}
                            disabled={saving}
                            style={getButtonStyle(true)}
                          >
                            {saving ? (
                              <>
                                {renderLoadingSpinner("sm")}
                                {isEditMode ? "Updating..." : "Adding..."}
                              </>
                            ) : isEditMode ? (
                              "Update"
                            ) : (
                              "Add"
                            )}
                          </button>
                          <button
                            onClick={clearFields}
                            disabled={saving}
                            style={getButtonStyle(false)}
                          >
                            {saving ? (
                              <>
                                {renderLoadingSpinner("sm")}
                                Clearing...
                              </>
                            ) : (
                              "Clear All"
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </section>
      </div>
      <AdminFooter isSidebarMinimized={isSidebarMinimized} />
    </>
  );
};

export default AddCategory;